import React, { useState, useEffect } from 'react';
import renderMarkdown from '../../utils/renderMarkdown';
import { formatTime } from '../../utils/formatTime';
import { Panel, Badge, Button } from '../../components/design';
import Icon from '../../components/Icon';

const CORE_API = '/api';

export default function TroubleshootTab({ serverId, server, queryClient }) {
  const [troubleshootMode, setTroubleshootMode] = useState('FAST');
  const [troubleshootPrompt, setTroubleshootPrompt] = useState('');
  const [troubleshootLoading, setTroubleshootLoading] = useState(false);
  const [troubleshootResult, setTroubleshootResult] = useState(null);
  const [troubleshootError, setTroubleshootError] = useState('');
  const [dots, setDots] = useState('.');

  useEffect(() => {
    if (!troubleshootLoading) return;
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '.' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, [troubleshootLoading]);

  const runTroubleshootAi = async () => {
    setTroubleshootLoading(true);
    setTroubleshootResult(null);
    setTroubleshootError('');
    try {
      const res = await fetch(`${CORE_API}/servers/${serverId}/troubleshoot/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: troubleshootMode, prompt: troubleshootPrompt }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Troubleshoot failed');
      }
      const data = await res.json();
      setTroubleshootResult(data);
    } catch (e) {
      setTroubleshootError(e.message);
    }
    setTroubleshootLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Panel title="AI Troubleshoot" actions={troubleshootLoading && <span className="muted" style={{ fontSize: 12 }}>Processing{dots}</span>}>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Describe the problem</label>
          <textarea value={troubleshootPrompt} onChange={e => setTroubleshootPrompt(e.target.value)}
            rows={3} placeholder="e.g., Server is running slow, high CPU usage, or describe any issue you're seeing..." style={{ resize: 'none' }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="muted" style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Analysis mode</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
            {[
              { id: 'FAST', label: 'Fast', desc: 'Quick analysis' },
              { id: 'DEEP_RCA', label: 'Deep RCA', desc: 'Root cause' },
              { id: 'SECURITY', label: 'Security', desc: 'Threat check' },
              { id: 'AUTONOMOUS_FIX', label: 'Auto Fix', desc: 'Fix commands' },
            ].map(m => (
              <button key={m.id} onClick={() => setTroubleshootMode(m.id)}
                style={{
                  padding: 12, borderRadius: 12, textAlign: 'left', cursor: 'pointer',
                  background: 'var(--glass)', border: troubleshootMode === m.id ? '1px solid var(--accent-1)' : '1px solid var(--glass-border)',
                }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: troubleshootMode === m.id ? 'var(--accent-1)' : 'var(--text-1)' }}>{m.label}</div>
                <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={runTroubleshootAi} disabled={troubleshootLoading}>
            {troubleshootLoading ? `Processing${dots}` : 'Run AI Analysis'}
          </Button>
          <Button variant="ghost" onClick={() => { setTroubleshootResult(null); setTroubleshootPrompt(''); }} disabled={troubleshootLoading}>Clear</Button>
        </div>
      </Panel>

      {troubleshootResult && troubleshootResult.diagnostics && (
        <Panel title="System Diagnostics" actions={<Badge tone="gray">{troubleshootResult.mode}</Badge>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {Object.entries(troubleshootResult.diagnostics).filter(([k]) => k !== 'ssh_error').slice(0, 12).map(([key, val]) => (
              <div key={key} style={{ padding: 10, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                <div className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{key.replace(/_/g, ' ')}</div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(val).slice(0, 80)}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {troubleshootResult && troubleshootResult.incidents && troubleshootResult.incidents.length > 0 && (
        <Panel title="Recent Incidents">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {troubleshootResult.incidents.slice(0, 5).map(inc => (
              <div key={inc.id} className="flex items-center gap-2" style={{ fontSize: 12 }}>
                <span style={{ width: 6, height: 6, borderRadius: 9999, background: inc.severity === 'critical' || inc.severity === 'high' ? 'var(--danger)' : inc.severity === 'medium' ? 'var(--warning)' : 'var(--info)' }} />
                <span style={{ color: 'var(--text-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.title}</span>
                <Badge tone={inc.status === 'open' ? 'red' : inc.status === 'resolved' ? 'green' : 'amber'}>{inc.status}</Badge>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {troubleshootResult && (
        <Panel title="AI Analysis Result" actions={<button onClick={() => setTroubleshootResult(null)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-3)' }}><Icon name="x" size={15} /></button>}>
          {troubleshootResult.error ? (
            <div style={{ fontSize: 14, color: 'var(--danger)' }}>{troubleshootResult.error}</div>
          ) : (
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{troubleshootResult.timestamp ? formatTime(troubleshootResult.timestamp) : ''}</div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(troubleshootResult.analysis) }} />
            </div>
          )}
        </Panel>
      )}

      {troubleshootError && !troubleshootResult && (
        <div style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid var(--danger)', background: 'rgba(248,113,113,0.10)', color: 'var(--danger)', fontSize: 14, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="alert-triangle" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 500 }}>Analysis failed</div>
            <div style={{ fontSize: 12, marginTop: 2, opacity: 0.8 }}>{troubleshootError}</div>
          </div>
        </div>
      )}
    </div>
  );
}
